import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm Alex from PropertySellEasy. How can I help you sell your property today? You can also call me directly at 1-888-334-3524 for voice assistance!",
      sender: "bot",
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText("");
    setIsTyping(true);

    // Simulate bot response based on REI Reply knowledge base
    setTimeout(() => {
      const botResponse = generateBotResponse(inputText);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: botResponse,
        sender: "bot",
        timestamp: new Date()
      }]);
      setIsTyping(false);
    }, 1500);
  };

  const generateBotResponse = (userInput) => {
    const input = userInput.toLowerCase();

    // Qualification questions based on REI Reply knowledge base
    if (input.includes('sell') || input.includes('house') || input.includes('property')) {
      return "Great! I'd love to help you with selling your property. Besides us reaching out today, what's got you considering selling this one?";
    }
    
    if (input.includes('cash') || input.includes('offer')) {
      return "We specialize in cash offers! What were you hoping to get out of your property? I can help you understand if we'd be a good fit.";
    }
    
    if (input.includes('timeline') || input.includes('when') || input.includes('time')) {
      return "Timeline is important! When are you looking to sell? Are you thinking ASAP, within a few weeks, or do you have more flexibility?";
    }
    
    if (input.includes('condition') || input.includes('repair') || input.includes('fix')) {
      return "No worries about condition - we buy houses as-is! Do you happen to have any recent interior photos? That would help us give you the most accurate offer.";
    }
    
    if (input.includes('price') || input.includes('worth') || input.includes('value')) {
      return "I understand pricing is important. What were you hoping to get out of it? We may not always be the highest, but we'll be the most convenient as long as you're flexible.";
    }
    
    if (input.includes('process') || input.includes('how') || input.includes('work')) {
      return "Our process is simple: 1) You tell us about your property, 2) We analyze it with AI and millions of data points, 3) You get a fair cash offer, 4) Close in 2-4 weeks. What questions do you have about the process?";
    }
    
    if (input.includes('location') || input.includes('where') || input.includes('state')) {
      return "We primarily operate in CA, MI, OH, AR, TX, GA, FL, TN, AL, AZ, MO, HI, MD, ID, IN, CO, and NV. Where is your property located?";
    }
    
    if (input.includes('commission') || input.includes('fee') || input.includes('cost')) {
      return "That's one of the best parts - no commissions, no fees, no closing costs! The cash offer we give you is exactly what you get. No surprises.";
    }
    
    // Default responses for engagement
    const defaultResponses = [
      "I understand. Can you tell me a bit more about your property situation?",
      "That makes sense. What's the biggest challenge you're facing with your property right now?",
      "Got it. How long have you been thinking about selling?",
      "I see. What would need to happen for this to be a good fit for you?"
    ];
    
    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110 animate-pulse"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="bg-white rounded-lg shadow-2xl w-80 h-96 flex flex-col border border-gray-200">
          {/* Header */}
          <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div>
              <h3 className="font-bold">PropertySellEasy Chat</h3>
              <p className="text-sm opacity-90">Alex - Property Specialist</p>
              <p className="text-xs opacity-75">📞 Call: 1-888-334-3524 (AI-Powered)</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`mb-4 ${
                  message.sender === 'user' ? 'text-right' : 'text-left'
                }`}
              >
                <div
                  className={`inline-block p-3 rounded-lg max-w-xs ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-white text-gray-800 rounded-bl-none shadow'
                  }`}
                >
                  <p className="text-sm">{message.text}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))}
            
            {isTyping && (
              <div className="text-left mb-4">
                <div className="inline-block p-3 rounded-lg bg-white shadow rounded-bl-none">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about selling your property..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim()}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Powered by PropertySellEasy AI
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;